import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShoppingBag, ArrowClockwise, CaretDown } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import FeaturedBundlesStrip from "@/pages/shop/FeaturedBundlesStrip";
import ShopFeaturedRow from "@/pages/shop/ShopFeaturedRow";
import ProductCard from "@/pages/shop/ProductCard";
import ProductModal from "@/pages/shop/ProductModal";
import { cartCount } from "@/lib/cart";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function EmptyState({ onRefresh }) {
  return (
    <div data-testid="shop-empty-state" className="border-2 border-white/15 bg-[#0F0F0F] p-10 md:p-16 text-center">
      <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-6">/ Fresh drop incoming</p>
      <h2 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
        Legacy merch<br />is on the way.
      </h2>
      <p className="mt-6 text-white/70 max-w-xl mx-auto text-base leading-relaxed">
        We&apos;re loading the next drop of Sweat with Slay Legacy tees, hats, and tribute pieces.
        The moment they go live in our print-on-demand studio, they&apos;ll appear right here — real-time.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button
          onClick={onRefresh}
          data-testid="shop-refresh"
          className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-[#B32027] hover:text-[#B32027] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] transition-colors"
        >
          <ArrowClockwise size={14} weight="bold" /> Check again
        </button>
        <Link
          to="/sweat-for-sacrifice#signup"
          data-testid="shop-signup-cta"
          className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em]"
        >
          Join the Legacy Wall <ArrowUpRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

/**
 * Full-screen image lightbox with click-to-zoom. Click cycles 1x → 2x → 3x → 1x.
 * When zoomed, drag to pan. Escape / X / backdrop click closes. Left/Right arrows
 * cycle between siblings when available.
 */
export default function Shop({ kmsMode = false }) {
  const [data, setData] = useState(null);
  const [tags, setTags] = useState({}); // { og_legacy: { label, heading, description, product_ids: [] } }
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [fromSignup, setFromSignup] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [bundleCount, setBundleCount] = useState(0);
  const [featuredBundles, setFeaturedBundles] = useState([]); // up to 3 bundle summaries
  const [bundleCatalog, setBundleCatalog] = useState({}); // catalog map for building bundle collages
  const [featuredProductId, setFeaturedProductId] = useState(null);
  // Category collapse/expand state — persists across page loads so a user who
  // opens Tees stays that way on their next visit. Default = everything
  // COLLAPSED (headings only) so the shop opens as a scannable table of
  // contents; the shopper expands whichever category they want.
  const COLLAPSED_KEY = "sws_shop_collapsed_v1";
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLLAPSED_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Default-collapsed helper: if the user has never explicitly toggled a
  // category, treat it as collapsed. Once they open it, we record `false`
  // in localStorage so it stays open on their next visit.
  const isCollapsed = (id) => (collapsed[id] === undefined ? true : !!collapsed[id]);
  const toggleCollapsed = (id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const collapseAll = (ids, on) => {
    const next = { ...collapsed };
    ids.forEach((id) => { next[id] = on; });
    setCollapsed(next);
    try { window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const load = () => {
    setRefreshing(true);
    // Always include KMS pieces so the regular /shop can surface them under
    // a dedicated "KMS Employees" section (fallback path for KMS folks who
    // land on the public shop). They stay filtered out of the main category
    // grid via the kmsProducts / publicProducts split below.
    const productsUrl = `${BACKEND}/api/shop/products?include_kms=1`;
    Promise.all([
      fetch(productsUrl).then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
      fetch(`${BACKEND}/api/product-tags`).then((r) => r.ok ? r.json() : { tags: {} }).catch(() => ({ tags: {} })),
    ])
      .then(([d, t]) => { setData(d); setTags(t?.tags || {}); })
      .catch((err) => {
        console.warn("Shop: products fetch failed:", err);
        setData({ products: [], count: 0, error: true, errorMessage: (err && err.message) || "network" });
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    // Always land at the top of the shop — some browsers restore scroll from prior nav.
    window.scrollTo(0, 0);
    load();
    // Fetch bundle count + first 3 featured bundle summaries for the "Featured"
    // strip that stays visible even when every category is collapsed.
    fetch(`${BACKEND}/api/bundles`)
      .then((r) => r.ok ? r.json() : { bundles: [], section: {} })
      .then((d) => {
        const bundles = d?.bundles || [];
        setBundleCount(bundles.length);
        setBundleCatalog(d?.catalog || {});
        const section = d?.section || {};
        let slugs = Array.isArray(section.featured_slugs) && section.featured_slugs.length
          ? section.featured_slugs
          : (section.featured_slug ? [section.featured_slug] : []);
        slugs = slugs.slice(0, 3);
        const bySlug = Object.fromEntries(bundles.map((b) => [b.slug, b]));
        const chosen = slugs.map((s) => bySlug[s]).filter(Boolean);
        if (chosen.length < 3) {
          for (const b of bundles) {
            if (chosen.length >= 3) break;
            if (!chosen.find((x) => x.slug === b.slug)) chosen.push(b);
          }
        }
        setFeaturedBundles(chosen);
      })
      .catch(() => { /* silent — Featured strip just hides bundles */ });
    // Fetch the pinned featured product so the tee tile shows on the
    // always-visible Featured strip.
    fetch(`${BACKEND}/api/shop/settings`)
      .then((r) => r.ok ? r.json() : {})
      .then((d) => setFeaturedProductId(d?.featured_product_id || null))
      .catch(() => { /* silent */ });
    // Detect ?from=signup — coming back from Sacrifice tribute form
    try {
      const url = new URL(window.location.href);
      // Deep-link: /shop?product=<id> opens the ProductModal directly so any
      // link (email, IG bio, share sheet) can point at a specific piece.
      const pParam = url.searchParams.get("product");
      if (pParam && /^\d+$/.test(pParam)) {
        setSelectedId(Number(pParam));
      }
      if (url.searchParams.get("from") === "signup") {
        setFromSignup(true);
      } else {
        // Also honor a persistent flag: if a pending signup exists we still show the banner
        const pending = window.localStorage.getItem("sws_pending_signup");
        if (pending) setFromSignup(true);
      }
    } catch (err) { console.warn("Press: shop banner localStorage read failed:", err); }
  }, []);

  // Filter out Printful template/prototype SKUs that shouldn't hit the storefront
  // (e.g. "(PLAIN NO LOGOS)" mock variants). These leak through when the admin
  // hasn't set them to Ignored inside Printful.
  const products = useMemo(() => {
    const raw = data?.products || [];
    return raw.filter((p) => {
      const n = (p?.name || "");
      if (/plain\s*no\s*logos?|no\s*logos?|template only|do not (list|show)/i.test(n)) return false;
      return true;
    });
  }, [data]);

  // KMS-exclusive products are surfaced as a dedicated top strip on /kms/shop
  // and NEVER appear in the normal category grid — they'd otherwise show
  // twice. The backend also filters them from the default /shop response, so
  // this is defensive.
  const { kmsProducts, publicProducts } = useMemo(() => {
    const kms = [];
    const pub = [];
    for (const p of products) {
      if (p.kms_exclusive) kms.push(p);
      else pub.push(p);
    }
    // Order KMS: t-shirt first, polo second, then anything else.
    kms.sort((a, b) => {
      const rank = (n) => {
        const s = (n || "").toLowerCase();
        if (/(t-?shirt|\btee\b)/.test(s)) return 0;
        if (/polo|golf/.test(s)) return 1;
        return 2;
      };
      return rank(a.name) - rank(b.name);
    });
    return { kmsProducts: kms, publicProducts: pub };
  }, [products]);

  // Categorization is content-derived — memoize so it doesn't re-run on unrelated re-renders
  // (opening the cart drawer, toggling a filter chip, etc.).
  // Order: OG Legacy first (tag-driven, multi-category) → tees → hoodies → …
  const { productsByCat, otherProducts, shownCategories } = useMemo(() => {
    const CATEGORIES = [
      { id: "all", label: "All" },
      // Bundles is a synthetic category — the actual bundle grid is rendered
      // inline via <FeaturedBundlesStrip /> when this section is expanded.
      { id: "bundles", label: "Bundles", match: () => false },
      // OG Legacy is TAG-driven (not name-matched). Populated below from tags.og_legacy.
      { id: "og_legacy", label: tags?.og_legacy?.label || "OG Legacy", match: () => false },
      // KMS Employees — synthetic category, populated from kmsProducts. Only
      // shown on the public /shop (not /kms/shop where the hero strip already
      // surfaces these). Gives KMS folks who forgot the /kms/shop URL a
      // labeled, collapsible category they can find alongside every other one.
      { id: "kms_employees", label: "KMS Employees", match: () => false },
      { id: "tees", label: "Tees & Tops", match: (n) => (/(\bt-?shirt\b|\btee\b|tank|long sleeve|(sacrifice|s4s|never forget)[-\s]*.*(pink|red|blue|black|white|grey|gray)?\s*logo|s4s.*(unisex|never forget)|og[-\s]+.*legacy|legacy\s+t\b)/i.test(n)) && !/polo|hood|jersey|baby|toddler|kids?|youth|onesie|golf|\bhat\b|cap|snapback|beanie|visor|towel|bandana|sticker|mug|bottle|tumbler|koozie|coozie|\bbag\b|tote|leggings|shorts|bra/i.test(n) },
      { id: "hoodies", label: "Hoodies & Sweats", match: (n) => /hood|sweat[\s-]?shirt|crewneck/i.test(n) && !/baby|toddler|kids?|youth/i.test(n) },
      { id: "jerseys", label: "Jerseys", match: (n) => /jersey/i.test(n) },
      { id: "polos", label: "Polos", match: (n) => /polo|golf/i.test(n) && !/towel|hat|cap|beanie|snapback|shorts|leggings|mug|bottle|tumbler|koozie|coozie|sticker|bandana/i.test(n) },
      { id: "activewear", label: "Activewear", match: (n) => /leggings|shorts|joggers|sports bra|bra|windbreaker|track/i.test(n) },
      { id: "headwear", label: "Headwear", match: (n) => /snapback|hat|cap|beanie|visor/i.test(n) },
      { id: "baby-kids", label: "Baby & Kids", match: (n) => /\b(baby|infant|toddler|kids?|youth|onesie|bib|romper|bodysuit)\b/i.test(n) },
      { id: "accessories", label: "Accessories", match: (n) => /tote|bag|backpack|wallet|pouch|scarf|socks|patch|band|towel/i.test(n) },
      { id: "drinkware", label: "Drinkware", match: (n) => /mug|bottle|tumbler|glass|flask|koozie|coozie|shaker/i.test(n) },
      { id: "prints", label: "Prints & Stickers", match: (n) => /sticker|poster|print|card|decal|banner/i.test(n) },
    ];
    const categorize = (p) => {
      const name = p.name || "";
      for (const c of CATEGORIES) {
        if (c.id === "all" || c.id === "bundles" || c.id === "og_legacy" || c.id === "kms_employees") continue; // synthetic / tag-driven
        if (c.match && c.match(name)) return c.id;
      }
      // Fall-through: no "More" category anymore — force everything into a
      // real category using name heuristics so nothing goes into a hidden bucket.
      const n = name.toLowerCase();
      if (/sticker|poster|print|card|decal|patch|pin|magnet/.test(n)) return "prints";
      if (/mug|bottle|tumbler|shaker|glass|flask|thermos|koozie|coozie/.test(n)) return "drinkware";
      if (/tote|bag|backpack|wallet|pouch|scarf|socks|band|towel|bandana|keychain/.test(n)) return "accessories";
      if (/hat|cap|beanie|snapback|visor/.test(n)) return "headwear";
      if (/leggings|shorts|joggers|sports bra|bra|windbreaker|track|pants/.test(n)) return "activewear";
      if (/hood|sweat[\s-]?shirt|crewneck/.test(n)) return "hoodies";
      if (/polo|golf/.test(n)) return "polos";
      if (/tank|tee|shirt|top|jersey|og[-\s]+.*legacy|legacy\s+t\b/.test(n)) return "tees";
      return "accessories"; // final catch-all
    };
    const buckets = {};
    for (const p of publicProducts) {
      const c = categorize(p);
      (buckets[c] = buckets[c] || []).push(p);
    }
    // Multi-category: also populate og_legacy bucket from the tag (products
    // remain in their normal bucket too).
    const ogIds = new Set((tags?.og_legacy?.product_ids || []).map(Number));
    if (ogIds.size > 0 && !tags?.og_legacy?.hidden) {
      buckets.og_legacy = publicProducts.filter((p) => ogIds.has(Number(p.id)));
    }
    // KMS Employees synthetic bucket — /shop only. On /kms/shop we skip so
    // the hero strip stays the single surface for these pieces.
    if (!kmsMode && kmsProducts.length > 0) {
      buckets.kms_employees = kmsProducts;
    }
    const shown = [
      CATEGORIES[0],
      ...CATEGORIES.slice(1).filter((c) => {
        if (c.id === "bundles") return bundleCount > 0;
        if (c.id === "kms_employees") return !kmsMode && kmsProducts.length > 0;
        return (buckets[c.id] || []).length > 0;
      }),
    ];
    return { productsByCat: buckets, otherProducts: [], shownCategories: shown };
  }, [publicProducts, tags, bundleCount, kmsMode, kmsProducts]);

  const filtered = useMemo(() => {
    if (activeCategory === "kms") return kmsProducts;
    if (activeCategory === "all") return publicProducts;
    return productsByCat[activeCategory] || [];
  }, [activeCategory, publicProducts, productsByCat, kmsProducts]);

  // og_legacy multi-category surfacing is handled in the productsByCat memo above.

  return (
    <main data-testid="shop-page" className="bg-[#0A0A0A] text-white font-body min-h-screen pt-20">
      <Nav />
      {/* Top bar */}
      <div className="border-b border-white/10 px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="shop-back-home"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027] transition-colors"
          >
            <ArrowLeft size={12} weight="bold" /> Home
          </Link>
          <div className="flex items-center gap-4">
          <Link
            to="/track"
            data-testid="shop-track-order"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027] transition-colors"
          >
            Track order
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="shop-refresh-top"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027] transition-colors disabled:opacity-40"
          >
            <ArrowClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Syncing…" : "Refresh"}
          </button>
          </div>
        </div>
      </div>

      {/* Pending-signup banner — user came from the Sacrifice tribute form */}
      {fromSignup && (
        <div data-testid="shop-from-signup-banner" className="bg-[#B32027] text-white px-6 md:px-12 py-4 border-b border-white/20">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold bg-[#0A0A0A] px-2 py-1 whitespace-nowrap">Step 2 of 2</span>
              <p className="text-sm md:text-base leading-snug">
                <span className="font-bold">Your entry is saved.</span> Pick a piece — your Legacy Wall spot locks in the moment your merch order clears.
              </p>
            </div>
            <Link
              to="/sweat-for-sacrifice"
              data-testid="shop-back-to-signup"
              className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/85 hover:text-white underline underline-offset-4"
            >
              ← Back to tribute page
            </Link>
          </div>
        </div>
      )}

      {/* KMS Exclusive banner — only ever renders in /kms/shop */}
      {kmsMode && (
        <div
          data-testid="kms-shop-banner"
          className="bg-gradient-to-r from-[#003366] via-[#0A0A0A] to-[#B32027] text-white px-5 md:px-12 py-5 border-b border-white/20"
        >
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
            <div className="text-[11px] md:text-[12px] uppercase tracking-[0.3em] font-bold text-white">
              KMS × Sweat With Slay · Exclusive team drop
            </div>
            <span className="text-white/70 text-[12px] md:text-sm leading-relaxed">
              Two team-only pieces below · every regular product still available
            </span>
          </div>
        </div>
      )}

      {/* KMS-exclusive top strip — renders FIRST on /kms/shop so it's the
          hero moment. Hidden when the "KMS" chip is active because the same
          2 products then show in the main grid below (no duplication). */}
      {kmsMode && kmsProducts.length > 0 && activeCategory !== "kms" && (
        <section
          data-testid="kms-exclusive-section"
          className="px-5 md:px-12 pt-10 md:pt-20 pb-10 md:pb-20 border-b border-white/10 bg-[#0F0F0F]"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-4">
              <div>
                <p className="text-[#FF1B91] uppercase tracking-[0.35em] text-[10px] font-bold mb-2 md:mb-3">/ KMS Technology · Team-only drop</p>
                <h1 className="font-display font-black text-4xl sm:text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">
                  KMS × Sweat<br />With Slay.
                </h1>
                <p className="text-white/70 text-sm md:text-lg mt-3 md:mt-4 max-w-xl leading-relaxed">
                  Two exclusive pieces for KMS Technology employees. Not listed on the public shop.
                </p>
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/45">
                {kmsProducts.length} piece{kmsProducts.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-5">
              {kmsProducts.map((p) => (
                <ProductCard key={p.id} p={p} onOpen={(x) => setSelectedId(x.id)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hero — for /shop this is the top of the page; for /kms/shop it
          comes AFTER the exclusive strip so the KMS pieces are the star. */}
      <section className="px-6 md:px-12 py-16 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="text-[#B32027] uppercase tracking-[0.4em] text-[11px] font-bold mb-4">
            {kmsMode ? "/ Also available · Full Legacy catalog" : "/ Sweat with Slay Legacy"}
          </p>
          <h2 className="font-display font-black text-6xl md:text-8xl uppercase tracking-tighter leading-[0.85]">
            Shop the<br />
            <span className="text-[#B32027]">tribute.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed">
            Every piece honors first responders. Net proceeds go 50/50 to Tunnel to Towers + FDNY Fire Family Transport. Live inventory from
            our Printful print-on-demand studio — updated in real time.
          </p>
        </div>
      </section>

      {/* Featured Legacy Bundles are now surfaced as a collapsible category
          inside the grid below (see "Bundles" section). */}

      {/* Grid or empty */}
      <section className="px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div data-testid="shop-loading" className="text-center py-20 text-white/60 uppercase tracking-[0.3em] text-xs">
              Loading products…
            </div>
          )}
          {!loading && data?.error && (
            <div data-testid="shop-load-error" className="border-2 border-[#B32027] bg-[#B32027]/10 p-8 text-center space-y-4 my-10">
              <p className="text-[#B32027] uppercase tracking-[0.3em] text-xs font-bold">/ Couldn&apos;t reach the store</p>
              <p className="text-white text-lg leading-relaxed max-w-md mx-auto">The Printful catalog didn&rsquo;t respond just now. Give it a second and hit refresh — this usually clears itself.</p>
              <button
                type="button"
                onClick={load}
                data-testid="shop-load-error-retry"
                disabled={refreshing}
                className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.3em] disabled:opacity-50"
              >
                <ArrowClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Trying again…" : "Try again"}
              </button>
            </div>
          )}
          {!loading && !data?.error && publicProducts.length === 0 && !kmsMode && (
            <EmptyState onRefresh={load} />
          )}
          {!loading && publicProducts.length > 0 && (
            <>
              <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/50">/ Live catalog</p>
                  <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter mt-2">
                    {publicProducts.length} piece{publicProducts.length === 1 ? "" : "s"} available
                  </h2>
                </div>
              </div>

              {/* Category filter — wraps naturally, no horizontal scroll */}
              <div data-testid="shop-category-filter" className="flex flex-wrap gap-2 pb-6 mb-10 border-b border-white/10">
                {/* KMS quick-filter chip — /kms/shop only. Selecting it
                    shows just the Tee + Polo in an isolated grid so KMS
                    employees can jump straight to their pair. */}
                {kmsMode && kmsProducts.length > 0 && (
                  <button
                    onClick={() => setActiveCategory("kms")}
                    data-testid="shop-category-chip-kms"
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border-2 transition-all ${
                      activeCategory === "kms"
                        ? "bg-[#FF1B91] border-[#FF1B91] text-white"
                        : "border-white/25 text-white/85 hover:border-[#FF1B91]"
                    }`}
                  >
                    KMS <span className="ml-1 opacity-80">{kmsProducts.length}</span>
                  </button>
                )}
                {/* Bundles is now a real category chip below — no separate
                    jump-link needed. */}
                {shownCategories.map((c) => {
                  let count;
                  if (c.id === "all") count = publicProducts.length;
                  else if (c.id === "bundles") count = bundleCount;
                  else if (c.id === "other") count = otherProducts.length;
                  else count = (productsByCat[c.id] || []).length;
                  const isActive = activeCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActiveCategory(c.id)}
                      data-testid={`shop-category-chip-${c.id}`}
                      className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border-2 transition-all ${
                        isActive
                          ? "bg-[#B32027] border-[#B32027] text-white"
                          : "bg-transparent border-white/15 text-white/70 hover:border-[#B32027] hover:text-[#B32027]"
                      }`}
                    >
                      {c.label} <span className={`ml-1 ${isActive ? "opacity-70" : "opacity-40"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Grouped grid — when "All" is active, each category is a
                  collapsible section. State persists in localStorage. */}
              {activeCategory === "all" ? (
                <>
                  {/* Always-visible Featured strip: 3 bundle tiles + pinned
                      product tile. Renders above the collapsed table-of-contents
                      so shoppers see hero pieces without expanding anything. */}
                  <ShopFeaturedRow
                    bundles={featuredBundles}
                    catalog={bundleCatalog}
                    featuredProduct={
                      featuredProductId
                        ? publicProducts.find((p) => Number(p.id) === Number(featuredProductId))
                        : null
                    }
                    onOpenBundles={() => {
                      // Expand the Bundles section + scroll to it so the shopper
                      // lands on the full build-your-bundle flow.
                      setCollapsed((prev) => {
                        const next = { ...prev, bundles: false };
                        try { window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
                        return next;
                      });
                      setTimeout(() => {
                        const el = document.querySelector('[data-testid="shop-section-bundles"]');
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 80);
                    }}
                    onOpenProduct={(pid) => setSelectedId(pid)}
                  />
                  {(() => {
                    const catIds = shownCategories.filter((c) => c.id !== "all").map((c) => c.id);
                    const allCollapsed = catIds.length > 0 && catIds.every((id) => isCollapsed(id));
                    return (
                      <div className="flex justify-end mb-4">
                        <button
                          type="button"
                          data-testid="shop-toggle-all-categories"
                          onClick={() => collapseAll(catIds, !allCollapsed)}
                          className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white inline-flex items-center gap-2"
                        >
                          {allCollapsed ? "Expand all" : "Collapse all"}
                          <CaretDown size={11} weight="bold" className={`transition-transform ${allCollapsed ? "" : "rotate-180"}`} />
                        </button>
                      </div>
                    );
                  })()}
                  <div className="space-y-6">
                    {shownCategories.filter((c) => c.id !== "all").map((c) => {
                      // Bundles is synthetic — count comes from bundleCount, body from FeaturedBundlesStrip.
                      const isBundles = c.id === "bundles";
                      const items = isBundles
                        ? []
                        : (c.id === "other" ? otherProducts : (productsByCat[c.id] || []));
                      if (!isBundles && items.length === 0) return null;
                      if (isBundles && bundleCount === 0) return null;
                      const sectionCollapsed = isCollapsed(c.id);
                      const count = isBundles ? bundleCount : items.length;
                      return (
                        <div
                          key={c.id}
                          data-testid={`shop-section-${c.id}`}
                          className="border-t border-white/10 pt-6"
                        >
                          <button
                            type="button"
                            onClick={() => toggleCollapsed(c.id)}
                            aria-expanded={!sectionCollapsed}
                            data-testid={`shop-section-toggle-${c.id}`}
                            className="w-full flex items-center justify-between mb-5 group text-left py-3 -my-1 min-h-[48px]"
                          >
                            <div className="flex items-center gap-4">
                              <CaretDown
                                size={18}
                                weight="bold"
                                className={`text-white/50 group-hover:text-white transition-transform ${sectionCollapsed ? "-rotate-90" : ""}`}
                              />
                              <h3 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl group-hover:text-[#B32027] transition-colors">
                                {c.label}
                              </h3>
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/45">
                              {count} piece{count === 1 ? "" : "s"}
                            </p>
                          </button>
                          {!sectionCollapsed && (
                            isBundles ? (
                              <FeaturedBundlesStrip />
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {items.map((p) => (
                                  <ProductCard key={p.id} p={p} onOpen={(x) => setSelectedId(x.id)} />
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : activeCategory === "bundles" ? (
                <FeaturedBundlesStrip />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map((p) => (
                    <ProductCard key={p.id} p={p} onOpen={(x) => setSelectedId(x.id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Legacy Wall reminder */}
      <section className="px-6 md:px-12 py-16 md:py-20 border-t border-white/10 bg-[#B32027] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase tracking-[0.4em] text-[10px] font-bold text-white/80 mb-4">/ Every purchase</p>
          <h3 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter leading-[0.9]">
            adds you to the<br />Legacy Wall.
          </h3>
          <p className="mt-5 text-white/90 text-base leading-relaxed">
            Net proceeds from Sweat With Slay Legacy merchandise are donated <span className="font-bold">equally between Tunnel to Towers Foundation and FDNY Fire Family Transport Foundation</span>, after costs directly associated with creating, operating, producing, processing, and fulfilling the Legacy Challenge and merchandise program.
          </p>
          <Link
            to="/sweat-for-sacrifice#signup"
            data-testid="shop-cta-signup"
            className="mt-6 inline-flex items-center gap-2 border-2 border-[#0A0A0A] bg-[#0A0A0A] hover:bg-transparent text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
          >
            Add a name → <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      </section>

      {selectedId != null && <ProductModal productId={selectedId} onClose={() => setSelectedId(null)} />}
    </main>
  );
}
